import React from 'react';
import { Box, Stack, Typography, useTheme, alpha } from '@mui/material';

import { SeatSnapshot } from '../../types/admissions';

/**
 * SeatSnapshotStrip — the seat budget above the merit list and the decision
 * board: offers out against the seat cap, as one bar plus three figures.
 * One canonical rendering so the numbers never read differently across tabs.
 */

interface Props { snapshot: SeatSnapshot; }

const SeatSnapshotStrip: React.FC<Props> = ({ snapshot }) => {
  const theme = useTheme();
  const cap = snapshot.hardCap;
  const pct = cap > 0 ? Math.min(100, (snapshot.live / cap) * 100) : 0;
  const tone = pct >= 100 ? theme.palette.error.main : pct >= 80 ? theme.palette.warning.main : theme.palette.success.main;

  return (
    <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 4 }} alignItems={{ md: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1 }}>{snapshot.live}</Typography>
            <Typography variant="body2" color="text.secondary">
              of {cap} seats offered{snapshot.overbookPercent > 0 && ` · cap includes ${snapshot.overbookPercent}% overbooking`}
            </Typography>
          </Stack>
          <Box sx={{ mt: 1.25, height: 8, borderRadius: 4, bgcolor: alpha(theme.palette.text.primary, 0.07), overflow: 'hidden' }}>
            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 4, bgcolor: tone, transition: 'width 300ms ease' }} />
          </Box>
        </Box>
        <Stack direction="row" spacing={3.5} sx={{ flexShrink: 0 }}>
          <Figure label="Seats" value={snapshot.totalSeats} />
          <Figure label="Seat cap" value={cap} />
          <Figure label="Left" value={snapshot.remaining} color={snapshot.remaining > 0 ? theme.palette.success.main : theme.palette.error.main} />
        </Stack>
      </Stack>
    </Box>
  );
};

const Figure: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
    <Typography variant="subtitle1" fontWeight={700} sx={{ color, lineHeight: 1.3 }}>{value}</Typography>
  </Box>
);

export default SeatSnapshotStrip;
